import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { MAX_SOCIAL_IMAGES, moveImage, type SocialImage } from '../lib/socialImages';
import { validateImageFile } from '../lib/imageValidation';

interface SocialImageStripProps {
  images: SocialImage[];
  busy: boolean;
  /** Voor de Instagram-waarschuwing: alleen dat platform vereist een beeld. */
  platform: string;
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
  onReorder: (images: SocialImage[]) => void;
  onError: (message: string) => void;
}

export function SocialImageStrip({
  images,
  busy,
  platform,
  onAdd,
  onRemove,
  onReorder,
  onError,
}: SocialImageStripProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const vol = images.length >= MAX_SOCIAL_IMAGES;

  function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);

    const ruimte = MAX_SOCIAL_IMAGES - images.length;
    if (ruimte <= 0) {
      onError(`Je kunt maximaal ${MAX_SOCIAL_IMAGES} beelden aan een post toevoegen.`);
      return;
    }

    const validaties = files.map((file) => ({ file, result: validateImageFile(file) }));
    const meldingen = validaties
      .map((v) => v.result)
      .filter((r): r is { ok: false; message: string } => !r.ok)
      .map((r) => r.message);

    const geldig = validaties.filter((v) => v.result.ok).map((v) => v.file);
    if (geldig.length > ruimte) {
      meldingen.push(`Er passen er nog ${ruimte} bij; de rest is overgeslagen.`);
    }
    if (meldingen.length > 0) onError(Array.from(new Set(meldingen)).join(' '));

    if (geldig.length > 0) onAdd(geldig.slice(0, ruimte));
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Beelden</h3>
        <span className="text-xs text-gray-500">
          {images.length} van {MAX_SOCIAL_IMAGES}
        </span>
      </div>

      <div
        className="flex flex-wrap gap-3"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          if (dragFrom !== null) return; // eigen tegel, geen bestand
          e.preventDefault();
          if (busy) return;
          handleFiles(e.dataTransfer.files);
        }}
      >
        {images.map((image, index) => (
          <div
            key={`${image.url}-${index}`}
            draggable={!busy}
            onDragStart={() => setDragFrom(index)}
            onDragEnd={() => setDragFrom(null)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (busy) return;
              if (dragFrom === null || dragFrom === index) return;
              onReorder(moveImage(images, dragFrom, index));
              setDragFrom(null);
            }}
            className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 cursor-move"
          >
            <img src={image.url} alt="" className="w-full h-full object-cover" />
            {index === 0 && (
              <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5">
                Cover
              </span>
            )}
            <button
              type="button"
              onClick={() => onRemove(index)}
              disabled={busy}
              aria-label="Beeld verwijderen"
              className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 text-gray-600 hover:text-red-600 disabled:opacity-50"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            {index > 0 && (
              <button
                type="button"
                onClick={() => onReorder(moveImage(images, index, index - 1))}
                disabled={busy}
                aria-label={`Beeld ${index + 1} naar voren verplaatsen`}
                className="absolute bottom-1 left-1 bg-white/90 rounded-full p-0.5 text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
            )}
            {index < images.length - 1 && (
              <button
                type="button"
                onClick={() => onReorder(moveImage(images, index, index + 1))}
                disabled={busy}
                aria-label={`Beeld ${index + 1} naar achteren verplaatsen`}
                className="absolute bottom-1 right-1 bg-white/90 rounded-full p-0.5 text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={vol || busy}
          className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-5 h-5" />
          <span className="text-[11px] mt-1">{busy ? 'Bezig…' : 'Toevoegen'}</span>
        </button>
      </div>

      <p className="text-xs text-gray-500 mt-2">
        Het eerste beeld is de cover en bepaalt de verhouding op Instagram. Sleep om
        de volgorde te wijzigen, of gebruik de pijltjes op een tegel.
      </p>

      {platform === 'instagram' && images.length === 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
          Een Instagram-post heeft een beeld nodig. Voeg er minstens één toe
          voordat je hem inplant.
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
